import axios from 'axios';
import API from '../config/API';

/**
 * A self-employed stylist's clients when they leave a salon (BE
 * salonClient/departureHandover.js, docs/client-flows.md §9c). The ONLY file
 * that knows the wire shapes.
 *
 *   GET  /v2/salon-client/departure-preview/:employeeId  → { selfEmployed, owned, coHeld, restoreWindowDays }
 *   GET  /v2/salon-client/departures                     → { result: [departure] }
 *   GET  /v2/salon-client/departures/:id/export          → { file (data: URI), filename, count }
 *   POST /v2/salon-client/departures/:id/import          → { added, alreadyYours, coHeld, failed }
 *   POST /v2/salon-client/departures/:id/restore         → { restored, already, taken, gone }
 */

function _headers() {
    return {
        'Content-Type': 'application/json',
        'authorization': 'Basic YmV1YXBwOmJldWFwcA==',
        'accessToken': localStorage.getItem('accessToken') || '',
    };
}

// Error envelopes come back as HTTP 200 { statusCode: 0, error: { responseMessage } }.
function _data(res) {
    const d = res && res.data;
    if (d && d.statusCode === 0) {
        const e = new Error((d.error && d.error.responseMessage) || 'Something went wrong. Please try again.');
        e.friendly = true;
        throw e;
    }
    return (d && d.responseData) || {};
}

function _fail(err) {
    if (err && err.friendly) throw err;
    const e = new Error('Something went wrong. Please try again.');
    e.friendly = true;
    throw e;
}

export function departurePreview(employeeId) {
    return axios.get(API.SALON_CLIENT_DEPARTURE_PREVIEW + encodeURIComponent(employeeId), { headers: _headers() })
        .then(_data)
        .then((d) => ({
            selfEmployed: d.selfEmployed === true,
            owned: Number(d.owned) || 0,
            coHeld: Number(d.coHeld) || 0,
            restoreWindowDays: Number(d.restoreWindowDays) || 90,
        }))
        .catch(_fail);
}

export function myDepartures() {
    return axios.get(API.SALON_CLIENT_DEPARTURES, { headers: _headers() })
        .then(_data)
        .then((d) => (Array.isArray(d.result) ? d.result : []).map((x) => ({
            id: String(x._id),
            salonName: x.salonName || 'your previous salon',
            at: x.at,
            total: Number(x.total) || 0,
            owned: Number(x.owned) || 0,
            coHeld: Number(x.coHeld) || 0,
            canImport: x.canImport === true,
            toImport: Number(x.toImport) || 0,
            canRestore: x.canRestore === true,
            restoreUntil: x.restoreUntil || null,
            restoredAt: x.restoredAt || null,
        })))
        .catch(_fail);
}

/** Downloads the CSV in the browser. */
export function downloadDeparture(id) {
    return axios.get(API.SALON_CLIENT_DEPARTURES + '/' + encodeURIComponent(id) + '/export', { headers: _headers() })
        .then(_data)
        .then((d) => {
            if (!d.file) throw Object.assign(new Error('Nothing to download.'), { friendly: true });
            const a = document.createElement('a');
            a.href = d.file;
            a.download = d.filename || 'your-clients.csv';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            return { count: Number(d.count) || 0 };
        })
        .catch(_fail);
}

export function importDeparture(id) {
    return axios.post(API.SALON_CLIENT_DEPARTURES + '/' + encodeURIComponent(id) + '/import', {}, { headers: _headers() })
        .then(_data)
        .catch(_fail);
}

export function restoreDeparture(id) {
    return axios.post(API.SALON_CLIENT_DEPARTURES + '/' + encodeURIComponent(id) + '/restore', {}, { headers: _headers() })
        .then(_data)
        .catch(_fail);
}
